import DocumentDownloader from '../components/DocumentDownloader';
import { useEffect, useState } from "react";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Trash2, ChevronDown, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AdminFiles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = () =>
    api
      .get("/admin/uploads")
      .then((r) => setItems(r.data.uploads))
      .catch((e) => toast.error(fmtError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this upload and generated files?")) return;
    try {
      await api.delete(`/admin/uploads/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  return (
    <div className="p-10 fade-up">
      <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">
        Output archive
      </p>
      <h1 className="font-display text-4xl mt-1">Generated files</h1>
      <p className="text-stone-600 text-sm mt-2">
        Every upload produces starred TC* and GS* PDFs that can be downloaded
        any time. Excel-only uploads expand to show per-semester files.
      </p>

      <div className="mt-8 space-y-4">
        {loading && <p className="text-stone-500">Loading…</p>}
        {!loading && items.length === 0 && (
          <Card className="p-10 text-center rounded-sm shadow-none border-stone-200">
            <p className="font-display text-xl text-stone-700">No files yet</p>
            <p className="text-sm text-stone-500 mt-1">
              Run an upload to generate starred TC* / GS* files.
            </p>
          </Card>
        )}
        {items.map((u) => {
          const hasSemesters = Array.isArray(u.semesters) && u.semesters.length > 0;
          const open = expanded[u.id];

          return (
            <Card
              key={u.id}
              data-testid={`file-row-${u.id}`}
              className="rounded-sm border-stone-200 shadow-none overflow-hidden bg-white"
            >
              {/* Header Row */}
              <div className="p-5 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-stone-900">
                      {u.program} ·{" "}
                      <span className="text-stone-600">{u.branch}</span>
                    </p>
                    {hasSemesters && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-indigo-950 text-white px-2 py-0.5 rounded-sm">
                        <Sparkles className="w-3 h-3" /> multi-sem
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone-500 mt-1 font-mono">
                    Batch {u.batch} ·{" "}
                    {hasSemesters ? "All sems" : `Sem ${u.semester || "—"}`} ·{" "}
                    {u.exam_session}
                  </p>
                  <p className="text-xs text-stone-400 mt-1 font-mono">
                    {new Date(u.created_at).toLocaleString()}
                  </p>
                </div>

                {/* Controls (Expand & Delete) */}
                <div className="flex items-center gap-2">
                  {hasSemesters && (
                    <Button
                      onClick={() =>
                        setExpanded((p) => ({ ...p, [u.id]: !open }))
                      }
                      size="sm"
                      variant="outline"
                      className="rounded-sm border-stone-300"
                    >
                      <ChevronDown
                        className={`w-3.5 h-3.5 mr-1.5 transition-transform ${
                          open ? "rotate-180" : ""
                        }`}
                      />
                      {open ? "Hide" : "Show"} files
                    </Button>
                  )}
                  <Button
                    onClick={() => remove(u.id)}
                    size="sm"
                    variant="ghost"
                    className="rounded-sm text-stone-400 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Body: Single Semester Fallback (Legacy) */}
              {!hasSemesters && (u.tc_file || u.gs_file) && (
                <div className="px-5 pb-5 flex flex-wrap gap-4 border-t border-stone-100 pt-5 bg-stone-50/50">
                  {u.tc_file && (
                    <DocumentDownloader 
                      documentType="Tabulation Chart"
                      fileName={u.tc_file}
                      baseUrl={`/api/admin/files/${u.id}/tc`} 
                    />
                  )}
                  {u.gs_file && (
                    <DocumentDownloader 
                      documentType="Grade Sheets (All)"
                      fileName={u.gs_file}
                      baseUrl={`/api/admin/files/${u.id}/gs`} 
                    />
                  )}
                </div>
              )}

              {/* Body: Multi-Semester Expanded View */}
              {hasSemesters && open && (
                <div className="border-t border-stone-200 bg-stone-50 p-5 space-y-4">
                  {u.semesters.map((s) => (
                    <div
                      key={s.semester}
                      className="bg-white border border-stone-200 rounded-md p-4 shadow-sm"
                    >
                      <div className="font-mono text-sm mb-3 pb-3 border-b border-stone-100 flex justify-between items-center">
                        <span className="font-bold text-stone-900">
                          Semester {s.semester}
                        </span>
                        <span className="text-stone-500 text-xs bg-stone-100 px-2 py-1 rounded-sm">
                          TC: {s.tc_count || 0} students · GS: {s.gs_count || 0} students
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-4">
                        {s.tc_file && (
                          <DocumentDownloader 
                            documentType="Tabulation Chart"
                            fileName={s.tc_file}
                            baseUrl={`/api/admin/files/${u.id}/sem/${s.semester}/tc`} 
                          />
                        )}
                        {s.gs_file && (
                          <DocumentDownloader 
                            documentType="Grade Sheets (All)"
                            fileName={s.gs_file}
                            baseUrl={`/api/admin/files/${u.id}/sem/${s.semester}/gs`} 
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
